import React, { useState } from 'react';
import { Checkbox, message } from 'antd';
import { useCookies } from 'react-cookie';
import { useNavigate, useParams } from 'react-router-dom';
import { utils, writeFile } from 'xlsx';
import dayjs from 'dayjs';
import {
  sortableContainer,
  sortableElement,
  sortableHandle,
} from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import { MenuOutlined } from '@ant-design/icons';
import { dlcApi, useDlcEpisodeList } from 'apis/story/dlcApis';
import { theme } from 'utils/style/theme';
import 'utils/style/sortDragging.css';
import { DlcEpisodeTypeList } from 'utils/constant';
import { scriptData } from 'apis/story/projectEpisodeApis';
import CapsuleButton from 'components/button/CapsuleButton';
import DefaultButton from 'components/button/DefaultButton';
import DeletePopConfirm from 'components/button/DeletePopConfirm';
import TextButton from 'components/button/TextButton';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import DLCEpisodeRegisterModal from './DLCEpisodeRegisterModal';
/**
 * * 작품 관리 - DLC - 에피소드 목록
 */

const columnName = [
  { name: 'scene_id', title: '사건 ID' },
  { name: 'template', title: '템플릿' },
  { name: 'speaker', title: '화자' },
  { name: 'script_data', title: '데이터' },
  { name: 'target_scene_id', title: '사건 이동' },
  { name: 'requisite', title: '조건' },
  { name: 'character_expression', title: '캐릭터 모션' },
  { name: 'emoticon_expression', title: '이모티콘' },
  { name: 'in_effect', title: '등장 효과' },
  { name: 'out_effect', title: '퇴장 효과' },
  { name: 'bubble_size', title: '말풍선 크기' },
  { name: 'bubble_pos', title: '말풍선 위치' },
  { name: 'bubble_hold', title: '말풍선 유지' },
  { name: 'bubble_reverse', title: '말꼬리 반전' },
  { name: 'control', title: '제어' },
  { name: 'voice', title: '음성' },
  { name: 'sound_effect', title: '효과음' },
  { name: 'autoplay_row', title: '자동진행' },
  { name: 'dev_comment', title: '메모' },
  { name: 'project_id', title: '프로젝트 ID' },
  { name: 'episode_id', title: '에피소드 ID' },
  { name: 'selection_group', title: '선택지 그룹' },
  { name: 'selection_no', title: '선택지 번호' },
  {
    name: 'max_selection_check',
    title: '전체 선택지 확인',
  },
];

// 정렬 아이콘
const DragHandle = sortableHandle(() => (
  <MenuOutlined style={{ cursor: 'pointer', color: '#999' }} />
));
const SortableItem = sortableElement((props) => <tr {...props} />);
const SortableContainer = sortableContainer((props) => <tbody {...props} />);

// ! ** START **/
const DLCEpisodeTable = ({
  dlcMainStatus,
  setDlcMainStatus,
  selectProject,
  episodeClick,
  setEpisodeClick,
  draggingStatus,
  setDraggingStatus,
}) => {
  const [cookies, setCookie, removeCookie] = useCookies();
  const { lang, dlcId } = cookies;
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [visible, setVisible] = useState(false);

  const [dlcEpisodeList, setDlcEpisodeList] = useState();
  const { data: dlcEpisodeListData } = useDlcEpisodeList(
    projectId,
    dlcId,
    setDlcEpisodeList,
  );

  /// 수정 페이지 이동
  const handleUpdateClick = (record) => () => {
    navigate(`update/${record?.episode_id}`, {
      state: {
        record: record,
        lang: lang,
        dataList: dlcEpisodeList && dlcEpisodeList,
        setEpisodeClick: setEpisodeClick(),
        setDlcMainStatus: setDlcMainStatus(),
      },
    });
  };

  /// 스크립트로 이동
  const handleScriptClick = (record) => () => {
    // 새 창으로 열기 위해 window.open()을 사용
    window.open(
      `/editor/${record.project_id}/contents/${record.episode_id}/?title=${
        selectProject?.title
      }&episode=${record.indexed_title}&lang=${
        lang !== undefined ? lang : 'KO' // 언어 기본값은 KO
      }`,
    );
  };

  /// 스크립트 다운로드
  const handleClickDownload = (record) => async () => {
    // 스크립트 데이터 불러오기
    const resScriptData = await scriptData(
      record.project_id,
      record.episode_id,
      lang,
    );
    // 컬럼명 배열에 답기
    const columnArrayName = [];
    const columnArrayTitle = [];
    columnName.forEach((v) => {
      columnArrayName.push(v.name);
      columnArrayTitle.push(v.title);
    });

    // header에 컬럼 순서를 지정해줌
    const ws = utils.json_to_sheet(resScriptData.script, {
      header: columnArrayName,
    });
    const wb = utils.book_new();

    // 컬럼을 한글로 바꾸자
    columnArrayTitle.forEach((v, i) => {
      const cellAdd = utils.encode_cell({ c: i, r: 0 });
      ws[cellAdd].v = v;
    });
    // 에피소드 id, 프로젝트 id 열 숨기기
    ws['!cols'] = [];
    ws['!cols'][19] = { hidden: true }; // 프로젝트 ID
    ws['!cols'][20] = { hidden: true };
    ws['!cols'][21] = { hidden: true };
    ws['!cols'][22] = { hidden: true };
    ws['!cols'][23] = { hidden: true };
    ws['!cols'][24] = { hidden: true };

    // 현재 시간 출력
    const today = dayjs().format('MMDD_HHmmss');
    utils.book_append_sheet(wb, ws, 'SHEET JS');
    writeFile(wb, `${record.indexed_title}_${today}.xlsx`);
  };

  /// 정렬 드래깅이 끝날 때 => api 호출
  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        [].concat(dlcEpisodeList),
        oldIndex,
        newIndex,
      ).filter((el) => !!el);
      // console.log('Sorted items >>>>> >> ', newData);
      let sortData = newData.map((v, i) => {
        v.sortkey = i;
        return v;
      });

      const response = await dlcApi.episodeSort(
        selectProject.project_id,
        dlcId,
        sortData,
      );

      if (!response) {
        return false;
      }

      setDraggingStatus(draggingStatus + 1);
      setDlcEpisodeList(sortData);
      message.success('에피소드가 재정렬 되었습니다.');
    }
  };
  const DraggableContainer = (props) => {
    return (
      <SortableContainer
        useDragHandle
        disableAutoscroll
        helperClass='row-dragging' // 정렬 아이콘 클릭 된 상태로 움직일 때의 모습 스타일링
        onSortEnd={onSortEnd}
        {...props}
      />
    );
  };
  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    // ***** int로 변환해주었어야 했다!
    const index = dlcEpisodeList.findIndex(
      (item) => parseInt(item.key) === parseInt(restProps['data-row-key']),
    );
    return <SortableItem index={index} {...restProps} />;
  };

  /// DLC 목록으로 돌아가기
  const handleGoBack = () => {
    navigate(`/story/${projectId}/dlc`, { state: { tabKey: 'dlc' } });
    setEpisodeClick(false);
    removeCookie('dlcId');
  };

  const handleOpen = () => {
    setVisible(true);
  };

  // const fetchData = async () => {
  //   const res = await dlcApi.episodeRead();
  // };
  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'episode_id',
      // width: '6rem',
      // align: 'center',
    },
    {
      title: '종류',
      dataIndex: 'episode_type',
      width: '5rem',
      render: (text) =>
        text === 'chapter' ? (
          <span>에피소드</span>
        ) : (
          text === 'ending' && <span>엔딩</span>
        ),
      filters: DlcEpisodeTypeList,
      onFilter: (value, record) => record.episode_type.indexOf(value) === 0,
    },
    {
      title: '에피소드 제목',
      dataIndex: 'indexed_title',
      render: (text, record) => (
        <TextButton onClick={handleUpdateClick(record)}>
          {record.indexed_title}
        </TextButton>
      ),
      // width: '6rem',
    },
    {
      title: '귀속 에피소드',
      dataIndex: 'depend_episode_title',
      // width: '6rem',
    },
    {
      title: '선택지 확정',
      dataIndex: 'selection_check',
      width: '6rem',
      align: 'center',
      render: (text, record) => (
        <Checkbox
          checked={record.selection_check === 1 ? true : false}
          disabled
        />
      ),
    },
    {
      title: '동작',
      // width: '6rem',
      align: 'center',
      render: (text, record) => (
        <div>
          <CapsuleButton
            onClick={handleScriptClick(record)}
            style={{ marginRight: `${theme.margins.marginRight}` }}
          >
            스크립트
          </CapsuleButton>
          {/* <CapsuleButton onClick={handleUpdateClick(record)}>
            수정
          </CapsuleButton> */}
          <CapsuleButton
            // disabled={selectProject?.auth}
            onClick={handleClickDownload(record)}
            style={{ marginRight: `${theme.margins.marginRight}` }}
          >
            다운로드
          </CapsuleButton>
          {/* <DeletePopConfirm
            title="해당 에피소드를 삭제하시겠습니까?"
            // onConfirm={handleDelete(record)}
            // disabled={selectProject?.auth}
          /> */}
        </div>
      ),
    },
    {
      title: '정렬',
      width: '5rem',
      align: 'center',
      render: () => <DragHandle disabled={selectProject?.auth} />,
    },
  ];
  return (
    <>
      <TableToolbar>
        <DefaultButton onClick={handleGoBack}>
          DLC 목록으로 돌아가기
        </DefaultButton>
        <div></div>
        <DefaultButton
          name='register'
          onClick={handleOpen}
          disabled={selectProject?.auth}
        >
          DLC 에피소드 등록
        </DefaultButton>
      </TableToolbar>
      <MainTable
        columns={columnsOrigin}
        dataSource={dlcEpisodeList}
        pagination={false}
        rowKey={(record) => `${record.key}`}
        components={
          dlcEpisodeList && {
            body: {
              wrapper: DraggableContainer,
              row: DraggableBodyRow,
            },
          }
        }
      />
      <DLCEpisodeRegisterModal
        projectId={projectId}
        dlcId={dlcId}
        visible={visible}
        setVisible={setVisible}
      />
    </>
  );
};

export default DLCEpisodeTable;
